/**
 * 4) « Mes chiffres » : mon compteur (SPEC MC5 ; retour M3 ; retours de Daphné du 07/10 ; demande de Luc du 08/10) —
 * maquette 17-compte. Calcul = celui de la v1 (lib/stats.ts, UserProfile.tsx Statistics), mêmes appels : GET
 * client/visits, client/zingfit-visits (avec strict=1 : hooks.ts useStrictZingfitVisits), site/sites, site/locations,
 * staff/coaches.
 *
 * Demande de Luc du 08/10 : « mettre chacune des marques plus en avant avec des codes couleurs différents ; le nombre
 * "tout Monday" et par année sont moins importants » (le programme de fidélité est par marque). Question de Luc du
 * 09/10 : « Quand pas de cours, on affiche pas le bloc peut-être sinon ça va faire vide ? » : la rubrique montre ce que
 * le membre a fait, pas ce qu'il n'a pas fait. Dans cet ordre :
 * 1. une carte par marque où le membre a des cours (lib/stats.ts brandCards puis shownBrandCards : marque du site
 *    d'abord), à la couleur de la marque (uxV2.brands ; jeton du site pour lui-même, comme le planning), texte AA
 *    (lib/brands.ts brandCardPalette) ; les marques sans cours n'ont plus de carte. Aucun cours nulle part (nouveau
 *    membre) : la seule carte de la marque du site, sobre : 0, « Pas encore de cours », lien vers le planning.
 *    Chargement (visites ou historique Stretch) : cette même carte, en attente (forme la plus probable : une marque).
 *    Visites OU historique en échec, rien en cache (décision de DA du 09/10 ; échec = erreur, ou pause hors ligne ;
 *    historique : GET client/zingfit-visits?strict=1, qui signale un historique Stretch illisible) : une seule carte
 *    neutre, « Chiffres indisponibles pour le moment », géométrie de la carte en attente, lien « Réessayer » ; ni
 *    « Tout Monday », ni « Cette année », ni préférés (sauf ceux déjà affichés pendant l'attente : ils restent, rien ne
 *    bouge, mais sans leur nombre de séances) ;
 * 2. « Tout Monday » et « Cette année », petits, neutres, seulement s'ils disent quelque chose (lib/stats.ts
 *    secondaryStats) ;
 * 3. studio et coach préférés (séances réalisées, toutes marques, clé (site, lieu) / (site, coach)), avec l'étiquette
 *    de leur marque et un liseré à sa couleur ; une autre marque s'ouvre sur son site, dans un nouvel onglet.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useListCoaches, useListLocations, useListVisits } from 'hooks/useApiQueries';
import {
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import type { ClientVisit } from 'types/client';
import { HTTP } from 'utils/http';
import { openCrossSite } from '../../../lib/crossSite';
import { hashUrl } from '../../../lib/planning/status';
import { cssThemeTokenReader, readSettings, resolveTokens } from '../../../theme/tokens';
import { IconExternal } from '../../Planning/icons';
import { brandCardPalette, injectedBrands, type BrandCardPalette, type BrandInfo } from '../lib/brands';
import { rereadFailedSource, sourceFailed } from '../lib/query';
import {
  brandCards,
  computeVisitStats,
  favoriteCountShown,
  favoritesShown,
  preferredCoach,
  preferredStudio,
  secondaryStats,
  shownBrandCards,
  statsStatus,
} from '../lib/stats';
import { readStrictZingfitVisits, STRICT_ZINGFIT_PATH, useStrictZingfitVisits, useVisitLookup } from '../hooks';
import { BrandTag, SectionHead } from './Ui';

/** Variables CSS d'une carte (ou d'un préféré) : couleurs de sa marque. */
const paletteVars = (palette: BrandCardPalette) =>
  ({
    '--mmv2-acct-brand': palette.accent,
    '--mmv2-acct-brand-surface': palette.surface,
    '--mmv2-acct-brand-on': palette.on,
    '--mmv2-acct-brand-text': palette.text,
  }) as CSSProperties;

/** Planning d'un studio / coach / d'une marque : route de l'app sur le site visité, site de la marque (nouvel onglet) sinon. */
function PlanningLink({
  brand,
  route,
  label,
  ariaLabel,
  className = 'mmv2-btn mmv2-btn--cta mmv2-btn--sm',
}: {
  brand: BrandInfo;
  route: string;
  label: string;
  ariaLabel: string;
  className?: string;
}) {
  if (brand.isCurrent) {
    return (
      <Link className={className} to={route} aria-label={ariaLabel}>
        {label}
      </Link>
    );
  }
  if (!brand.url) return null;
  const href = hashUrl(brand.url, route);
  return (
    <a
      className={className}
      href={href}
      target="_blank"
      rel="noopener"
      aria-label={`${ariaLabel} chez ${brand.name}, nouvel onglet`}
      onClick={(event) => {
        event.preventDefault();
        openCrossSite(href, String(brand.siteId));
      }}
    >
      {label}
      <IconExternal className="mmv2-acct-visit__ext" />
    </a>
  );
}

/**
 * Carte d'une marque : pleine à sa couleur avec des cours ; sobre (papier, liseré) sans cours (0, lien vers son
 * planning) et en attente (« – », sans lien : même hauteur que la carte pleine).
 */
function BrandCard({
  brand,
  count,
  palette,
  loading,
}: {
  brand: BrandInfo;
  count: number;
  palette: BrandCardPalette;
  loading: boolean;
}) {
  const empty = loading || count === 0;
  const cls = `mmv2-acct-brandcard${empty ? ' mmv2-acct-brandcard--empty' : ''}${loading ? ' mmv2-acct-brandcard--pending' : ''}`;
  return (
    <li className={cls} data-brand={brand.siteId} style={paletteVars(palette)}>
      <span className="mmv2-acct-brandcard__name">{brand.name}</span>
      <b className="mmv2-acct-brandcard__n" aria-busy={loading || undefined}>
        {loading ? (
          // « 00 » invisible sous le « – », dans la même case (account.css) : largeur de deux chiffres réservée, le nom
          // ne glisse pas quand un compteur de 10 à 99 arrive (relecture du 09/10).
          <>
            <span>–</span>
            <span className="mmv2-acct-brandcard__ghost" aria-hidden="true">
              00
            </span>
          </>
        ) : (
          count
        )}
      </b>
      <span className="mmv2-acct-brandcard__unit">
        {loading ? 'Cours réalisés' : count === 0 ? 'Pas encore de cours' : count > 1 ? 'Cours réalisés' : 'Cours réalisé'}
      </span>
      {!loading && count === 0 ? (
        <PlanningLink
          brand={brand}
          route="/"
          label="Voir le planning"
          // Autre marque : PlanningLink ajoute « chez <marque>, nouvel onglet ».
          ariaLabel={brand.isCurrent ? `Voir le planning de ${brand.name}` : 'Voir le planning'}
          className="mmv2-linkish mmv2-acct-brandcard__link"
        />
      ) : null}
    </li>
  );
}

/**
 * Signale, au retrait de `ref` (démontage), que le focus était dedans : le navigateur le laisse alors sur le document.
 * Nettoyage d'un effet de mise en page : il passe AVANT le retrait des nœuds (React 18), le focus y est encore lisible.
 * `onFocusLost` doit être stable (useCallback).
 */
function useFocusLossOnUnmount(ref: RefObject<HTMLElement>, onFocusLost: () => void) {
  useLayoutEffect(() => {
    const node = ref.current;
    return () => {
      if (node && node.contains(document.activeElement)) onFocusLost();
    };
  }, [ref, onFocusLost]);
}

/** Liste des cartes de marque (« Cours réalisés par marque »), retirée quand les chiffres deviennent indisponibles. */
function BrandCardList({
  listRef,
  count,
  onFocusLost,
  children,
}: {
  listRef: RefObject<HTMLUListElement>;
  count: number;
  onFocusLost: () => void;
  children: ReactNode;
}) {
  useFocusLossOnUnmount(listRef, onFocusLost);
  return (
    <ul
      ref={listRef}
      className={`mmv2-acct-brandcards mmv2-acct-brandcards--n${Math.min(count, 4)}`}
      aria-label="Cours réalisés par marque"
    >
      {children}
    </ul>
  );
}

/**
 * Chiffres indisponibles (décision de DA du 09/10 : visites ou historique Stretch en erreur, rien en cache). Une seule
 * carte, de la géométrie de la carte en attente : mêmes classes (bandeau, « – » sur la largeur de deux chiffres), le
 * texte prend la place du nom et du libellé, le lien celle du lien de la carte d'invitation (à droite, sur la ligne de
 * base). Neutre (account.css, --unavailable) : on ne sait rien des marques du membre. Insérée dans la région
 * role="status" de la rubrique : le message est lu (poliment). « Réessayer » relit les sources en erreur ; pendant la
 * lecture, la carte reste (bouton inactif, aria-disabled : il garde le focus) ; un nouvel échec relit le message
 * (`failures`). Carte retirée pendant que son bouton a le focus : `onFocusLost` (Stats le replace).
 */
function UnavailableCard({
  busy,
  failures,
  onRetry,
  retryRef,
  onFocusLost,
}: {
  busy: boolean;
  failures: number;
  onRetry: () => void;
  retryRef: RefObject<HTMLButtonElement>;
  onFocusLost: () => void;
}) {
  useFocusLossOnUnmount(retryRef, onFocusLost);
  return (
    <div className="mmv2-acct-brandcards mmv2-acct-brandcards--n1">
      <div className="mmv2-acct-brandcard mmv2-acct-brandcard--empty mmv2-acct-brandcard--pending mmv2-acct-brandcard--unavailable">
        <b className="mmv2-acct-brandcard__n" aria-hidden="true">
          <span>–</span>
          <span className="mmv2-acct-brandcard__ghost">00</span>
        </b>
        {/* Espaces entre les textes : ignorés par la grille, ils séparent les mots pour les lecteurs d'écran (sinon
            « indisponiblespour »). */}
        <span key={`name-${failures}`} className="mmv2-acct-brandcard__name">
          Chiffres indisponibles
        </span>{' '}
        <span key={`unit-${failures}`} className="mmv2-acct-brandcard__unit">
          pour le moment
        </span>{' '}
        <button
          ref={retryRef}
          type="button"
          className="mmv2-linkish mmv2-acct-brandcard__link"
          aria-disabled={busy || undefined}
          onClick={onRetry}
        >
          Réessayer
        </button>
      </div>
    </div>
  );
}

/**
 * Studio ou coach préféré : liseré à la couleur de sa marque (rappel discret) ; étiquette de la marque quand la ville
 * en a plusieurs (ou pour une autre marque).
 */
function Favorite({
  brand,
  palette,
  showTag,
  label,
  value,
  link,
}: {
  brand: BrandInfo;
  palette: BrandCardPalette;
  showTag: boolean;
  label: string;
  value: ReactNode;
  link: ReactNode;
}) {
  return (
    <div
      className="mmv2-acct-stat mmv2-acct-stat--brand"
      data-brand={brand.siteId}
      style={{ '--mmv2-acct-accent': palette.accent } as CSSProperties}
    >
      <span>{label}</span>
      <b className="mmv2-acct-stat__fav">{value}</b>
      {showTag ? <BrandTag brand={{ ...brand, color: palette.accent }} /> : null}
      {link}
    </div>
  );
}

export function Stats() {
  const queryClient = useQueryClient();
  const visitsQuery = useListVisits() || {};
  const zingfitQuery = useStrictZingfitVisits() || {};
  const { data: visits } = visitsQuery;
  const { data: zingfitVisits } = zingfitQuery;
  // « Réessayer » : la carte d'indisponibilité reste pendant la nouvelle lecture.
  const [retrying, setRetrying] = useState(false);
  const [failures, setFailures] = useState(0);
  // Focus laissé sur le document par le retrait du bouton « Réessayer » ou de la liste des cartes (relecture du 09/10).
  const focusLost = useRef(false);
  const onFocusLost = useCallback(() => {
    focusLost.current = true;
  }, []);
  const retryRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // En attente tant qu'une des deux sources du compteur manque (relecture du 09/10). Les visites sont souvent déjà en
  // cache (le planning les charge) quand l'historique Stretch, demandé par Mon compte seul, n'est pas encore arrivé :
  // sans cela, la rubrique montrait comme définitif un état incomplet (une carte, puis trois ; « 0 · Pas encore de
  // cours » chez un ancien membre Zingfit). Une des deux en échec sans cache, erreur ou pause hors ligne (lib/query.ts) :
  // chiffres indisponibles (décision de DA du 09/10 ; avant : trois cartes « 0 », ou les visites seules).
  const status = retrying ? 'unavailable' : statsStatus(visitsQuery, zingfitQuery);
  const isLoading = status === 'pending';
  const unavailable = status === 'unavailable';
  const { data: studios } = useListLocations();
  const { data: coaches } = useListCoaches();
  const { brandOf, studioName, currentSiteId } = useVisitLookup();
  const regionalSiteIds = typeof mondayMindbody !== 'undefined' ? mondayMindbody?.regionalSiteIds : undefined;
  // Couleur, encre et papier du site (même calcul que mountV2 et le planning).
  const site = useMemo(() => resolveTokens(readSettings(), cssThemeTokenReader()), []);
  const citySiteIds = useMemo(() => injectedBrands().map((b) => Number(b.siteId)), []);

  const stats = useMemo(
    () => computeVisitStats(visits, zingfitVisits, regionalSiteIds),
    [visits, zingfitVisits, regionalSiteIds],
  );
  const studio = useMemo(() => preferredStudio(visits, studios, regionalSiteIds), [visits, studios, regionalSiteIds]);
  const coach = useMemo(() => preferredCoach(visits, coaches, regionalSiteIds), [visits, coaches, regionalSiteIds]);

  /** Couleurs d'une marque : jeton du site pour lui-même (un seul rouge sur la page), couleur de uxV2.brands sinon. */
  const paletteOf = useMemo(
    () => (brand: BrandInfo) => brandCardPalette(brand.isCurrent ? site.brand : brand.color, site),
    [site],
  );

  /** Marques de la ville (et de la région où le membre a des cours), avec leur compteur, 0 compris. */
  const cityCards = useMemo(
    () => brandCards(isLoading ? {} : stats.bySite, citySiteIds, currentSiteId),
    [isLoading, stats.bySite, citySiteIds, currentSiteId],
  );
  const cards = useMemo(
    () =>
      unavailable
        ? []
        : shownBrandCards(cityCards).map((card) => {
            const brand = brandOf(card.siteId);
            return { ...card, brand, palette: paletteOf(brand) };
          }),
    [unavailable, cityCards, brandOf, paletteOf],
  );
  const secondary = status === 'ready' ? secondaryStats(stats) : null;
  // Étiquette de marque des préférés dès que la ville a plusieurs marques (cartes affichées ou non), comme avant.
  const multiBrand = cityCards.length > 1;
  // Préférés pendant l'indisponibilité : état d'avant figé (lib/stats.ts favoritesShown), CHACUN pour lui (relecture du
  // 09/10 : un coach dont la fiche arrivait après l'erreur apparaissait sous « Chiffres indisponibles », +180 px). État
  // du rendu précédent, mis à jour pendant le rendu (motif « informations des rendus précédents » de React).
  const [shownBefore, setShownBefore] = useState({ studio: false, coach: false });
  const showStudio = favoritesShown(shownBefore.studio, status, !!studio);
  const showCoach = favoritesShown(shownBefore.coach, status, !!coach);
  if (showStudio !== shownBefore.studio || showCoach !== shownBefore.coach) {
    setShownBefore({ studio: showStudio, coach: showCoach });
  }
  const studioBrand = studio && showStudio ? brandOf(studio.siteId) : null;
  const coachBrand = coach && showCoach ? brandOf(coach.siteId) : null;
  const showStudioCount = favoriteCountShown(status);

  /**
   * Relit les sources en échec (lib/query.ts rereadFailedSource : réponse posée dans le cache, sans repasser la requête
   * en attente) ; la carte reste, bouton inactif, jusqu'aux réponses.
   */
  const retry = () => {
    if (retrying) return;
    const reads = [
      sourceFailed(visitsQuery)
        ? rereadFailedSource(
            queryClient,
            ['/client/visits'],
            () => HTTP.get<ClientVisit[]>('/client/visits').send(),
            visitsQuery.refetch,
          )
        : true,
      sourceFailed(zingfitQuery)
        ? rereadFailedSource(queryClient, [STRICT_ZINGFIT_PATH], readStrictZingfitVisits, zingfitQuery.refetch)
        : true,
    ];
    setRetrying(true);
    void Promise.all(reads).then((read) => {
      // Nouvel échec : le bouton garde le focus ; le message, remonté, est relu par la région role="status".
      if (read.includes(false)) setFailures((count) => count + 1);
      setRetrying(false);
    });
  };

  // Bouton « Réessayer » ou liste des cartes retiré(e) avec le focus (chiffres arrivés ; carte en attente quand les
  // visites arrivent avant l'historique Stretch ; carte d'indisponibilité quand il échoue ensuite) : focus sur ce qui
  // les remplace, la liste des cartes (en attente ou chiffres) ou le bouton ; jamais laissé sur le document.
  useEffect(() => {
    if (retrying || !focusLost.current) return;
    focusLost.current = false;
    const active = document.activeElement;
    if (active && active !== document.body) return;
    const target = status === 'unavailable' ? retryRef.current : listRef.current;
    if (!target) return;
    if (target === listRef.current) target.tabIndex = -1;
    target.focus({ preventScroll: true });
  }, [retrying, status]);

  return (
    <section className="mmv2-acct-section" aria-labelledby="mmv2-acct-stats">
      <SectionHead id="mmv2-acct-stats" title="Mes chiffres" />

      {/* Région vive toujours présente (vide hors erreur, sans hauteur) : la carte d'indisponibilité y est insérée,
          donc lue ; une région créée avec son contenu ne l'est pas toujours. */}
      <div className="mmv2-acct-statstatus" role="status">
        {unavailable ? (
          <UnavailableCard
            busy={retrying}
            failures={failures}
            onRetry={retry}
            retryRef={retryRef}
            onFocusLost={onFocusLost}
          />
        ) : null}
      </div>

      {cards.length ? (
        <BrandCardList listRef={listRef} count={cards.length} onFocusLost={onFocusLost}>
          {cards.map(({ siteId, count, brand, palette }) => (
            <BrandCard key={siteId} brand={brand} count={count} palette={palette} loading={!!isLoading} />
          ))}
        </BrandCardList>
      ) : null}

      {secondary ? (
        <div className="mmv2-acct-statfoot">
          {secondary.total !== null || secondary.thisYear !== null ? (
            <dl className="mmv2-acct-statline">
              {secondary.total !== null ? (
                <div className="mmv2-acct-statline__item">
                  <dt>Tout Monday</dt>
                  <dd>
                    <b>{secondary.total}</b> cours
                  </dd>
                </div>
              ) : null}
              {secondary.thisYear !== null ? (
                <div className="mmv2-acct-statline__item">
                  <dt>Cette année</dt>
                  <dd>
                    <b>{secondary.thisYear}</b> cours
                  </dd>
                </div>
              ) : null}
            </dl>
          ) : null}
          <p className="mmv2-acct-statnote mmv2-tiny mmv2-muted">
            Cours réalisés, comptés dans la marque où ils ont eu lieu, sans les absences ni les annulations tardives.
          </p>
        </div>
      ) : null}

      {studioBrand || coachBrand ? (
        <div className="mmv2-acct-statgrid mmv2-acct-statgrid--fav">
          {studio && studioBrand ? (
            <Favorite
              brand={studioBrand}
              palette={paletteOf(studioBrand)}
              showTag={multiBrand || !studioBrand.isCurrent}
              label="Mon studio préféré"
              value={
                <>
                  {studioName(studio.location.Name)}{' '}
                  {showStudioCount ? (
                    <small>({studio.count})</small>
                  ) : (
                    // Chiffres indisponibles : nombre invisible et muet, à sa place (lib/stats.ts favoriteCountShown).
                    <small className="mmv2-acct-stat__count--hidden" aria-hidden="true">
                      ({studio.count})
                    </small>
                  )}
                </>
              }
              link={
                <PlanningLink
                  brand={studioBrand}
                  route={`/studio/${studio.location.Id}`}
                  label="Voir le planning"
                  ariaLabel={`Voir le planning du studio ${studioName(studio.location.Name)}`}
                />
              }
            />
          ) : null}
          {coach && coachBrand ? (
            <Favorite
              brand={coachBrand}
              palette={paletteOf(coachBrand)}
              showTag={multiBrand || !coachBrand.isCurrent}
              label="Mon coach préféré"
              value={coach.coach.FirstName}
              link={
                <PlanningLink
                  brand={coachBrand}
                  route={`/coach/${coach.coach.Id}`}
                  label="Voir son planning"
                  ariaLabel={`Voir le planning de ${coach.coach.FirstName}`}
                />
              }
            />
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
